<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';

import UErrorView from '@/shared/components/UErrorView.vue';
import { useError } from '@/shared/composables/useError';
import { isFocusMode } from '@/shared/composables/useFocusMode';
import AppNav from '@/shared/layouts/AppNav.vue';
import PageNav from '@/shared/layouts/PageNav.vue';
import { findNavLink, navTrail } from '@/shared/navigation';
import { useAuthStore } from '@/stores/auth';

import type { DropdownMenuItem } from '@nuxt/ui';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

const route = useRoute();
const auth = useAuthStore();

/**
 * Two shapes of the same shell:
 *
 *   ≥ lg  a white sidebar with the whole table of contents, always visible —
 *         on a desktop there is room, and a menu you can SEE is a menu you
 *         use; the page scrolls on its own to the right of it. It is as wide
 *         as the drawer (20rem), so a label that fits one fits the other —
 *         at 18rem the active «Тренажёр: неправильные» with its marker took
 *         two lines.
 *   < lg  a sticky top bar (menu · where am I) and the same list in a drawer.
 *
 * The WINDOW scrolls, not a panel, so the router's `scrollBehavior` resets
 * each new page to the top and mobile browsers can hide their toolbars.
 */
const mobileNavOpen = ref(false);

// A viewport that crosses the breakpoint with the drawer open would keep the
// overlay and the scroll lock with nothing visible to dismiss them.
const isDesktop = useMediaQuery('(min-width: 64rem)');

watch(isDesktop, (desktop) => {
  if (desktop) {
    mobileNavOpen.value = false;
  }
});

// Back/forward change the route without a tap on the drawer.
watch(() => route.fullPath, () => {
  mobileNavOpen.value = false;
});

const current = computed(() => findNavLink(route.path));

/**
 * On a phone the on-screen keyboard takes half the screen, and the browser
 * only scrolls a focused field to just above it — the «Проверить» button and
 * the feedback under the field end up hidden. Once the keyboard has opened,
 * bring the field to the middle of what is left, so the question above it
 * and the button below it stay in view.
 *
 * A screen that knows its own shape marks the block to keep in view with
 * `data-keyboard-anchor` (a trainer card: question, field, button); that block
 * is aligned to the top instead, so the question above the field survives too.
 * It is aligned only while the keyboard is actually up: a trainer focuses its
 * field on mount, before any keyboard, and the tap that later opens the
 * keyboard fires no new `focus` — the viewport resize is what announces it.
 */
const KEYBOARD_SETTLE_MS = 350;
/** A visible area under this share of the screen means the keyboard is open. */
const KEYBOARD_OPEN_RATIO = 0.75;
let focusTimer: ReturnType<typeof setTimeout> | null = null;

const isKeyboardOpen = () => {
  const viewport = window.visualViewport;
  return Boolean(viewport) && viewport!.height < window.screen.height * KEYBOARD_OPEN_RATIO;
};

const keepFieldInView = (target: HTMLElement, anchorOnly: boolean) => {
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  if (focusTimer) clearTimeout(focusTimer);
  focusTimer = setTimeout(() => {
    if (document.activeElement !== target) return;
    const anchor = target.closest<HTMLElement>('[data-keyboard-anchor]');
    if (anchor) {
      if (isKeyboardOpen()) anchor.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else if (!anchorOnly) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, KEYBOARD_SETTLE_MS);
};

const centerFocusedField = (event: FocusEvent) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
  keepFieldInView(target, false);
};

const onViewportResize = () => {
  const target = document.activeElement;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
  keepFieldInView(target, true);
};

onMounted(() => {
  document.addEventListener('focusin', centerFocusedField);
  window.visualViewport?.addEventListener('resize', onViewportResize);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', centerFocusedField);
  window.visualViewport?.removeEventListener('resize', onViewportResize);
  if (focusTimer) clearTimeout(focusTimer);
});

const displayName = computed(() => auth.user?.fullName?.trim() || auth.user?.email || '');

const userMenuItems = computed<DropdownMenuItem[][]>(() => [
  [{ label: auth.user?.email ?? '', type: 'label' }],
  [{
    label: 'Выйти',
    icon: 'i-lucide-log-out',
    onSelect: () => {
      mobileNavOpen.value = false;
      void auth.logout();
    },
  }],
]);
</script>

<template>
  <!-- Critical error: replace the whole shell, there is nothing to navigate to. -->
  <UErrorView v-if="hasError && isCriticalError" />

  <div
    v-else
    class="min-h-svh"
  >
    <!-- Desktop sidebar -->
    <aside
      class="fixed inset-y-0 start-0 z-30 hidden w-80 flex-col border-e border-default bg-elevated lg:flex"
      data-testid="sidebar"
    >
      <RouterLink
        to="/"
        class="flex h-16 shrink-0 items-center gap-3 px-5"
      >
        <span class="flex size-9 items-center justify-center rounded-md bg-primary text-white shadow-card">
          <UIcon
            name="i-lucide-book-open-text"
            class="size-5"
          />
        </span>
        <span class="flex flex-col leading-tight">
          <span class="text-base font-bold tracking-tight text-highlighted">DailyCzech</span>
          <span class="text-xs text-muted">чешский каждый день</span>
        </span>
      </RouterLink>

      <div class="min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-2">
        <AppNav />
      </div>

      <div class="shrink-0 border-t border-default p-3">
        <UDropdownMenu
          :items="userMenuItems"
          :content="{ side: 'top', align: 'start' }"
          :ui="{ content: 'w-64' }"
        >
          <UButton
            color="neutral"
            variant="ghost"
            block
            trailing-icon="i-lucide-chevrons-up-down"
            data-testid="sidebar-user-button"
            :ui="{ base: 'min-h-11 justify-start gap-3 px-2.5 font-medium', trailingIcon: 'ms-auto size-4 text-dimmed' }"
          >
            <UAvatar
              :alt="displayName"
              size="sm"
              :ui="{ root: 'bg-primary/10', fallback: 'font-semibold text-primary' }"
            />
            <span
              class="truncate text-sm text-highlighted"
              data-testid="sidebar-user-name"
            >{{ displayName }}</span>
          </UButton>
        </UDropdownMenu>
      </div>
    </aside>

    <div class="flex min-h-svh min-w-0 flex-col lg:ps-80">
      <!-- Mobile top bar -->
      <header
        class="sticky top-0 z-30 flex min-h-14 shrink-0 items-center gap-1 border-b border-default bg-elevated/90 px-1.5 backdrop-blur-md lg:hidden"
        :style="{ paddingTop: 'var(--safe-area-top)' }"
      >
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-menu"
          aria-label="Открыть меню"
          data-testid="mobile-nav-toggle"
          :ui="{ base: 'size-11 justify-center', leadingIcon: 'size-6' }"
          @click="mobileNavOpen = true"
        />
        <RouterLink
          v-if="!current"
          to="/"
          class="flex min-h-11 items-center gap-2"
        >
          <span class="flex size-7 items-center justify-center rounded-[9px] bg-primary text-white">
            <UIcon
              name="i-lucide-book-open-text"
              class="size-4"
            />
          </span>
          <span class="text-base font-bold tracking-tight text-highlighted">DailyCzech</span>
        </RouterLink>
        <div
          v-else
          class="flex min-w-0 items-center gap-2.5"
        >
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-[9px]"
            :class="current.section.tone.tile"
          >
            <UIcon
              :name="current.section.icon"
              class="size-4"
            />
          </span>
          <span class="flex min-w-0 flex-col leading-tight">
            <span class="truncate text-xs text-muted">{{ navTrail(current) }}</span>
            <span class="truncate text-sm font-semibold text-highlighted">{{ current.link.title }}</span>
          </span>
        </div>
      </header>

      <!-- Non-critical error: rendered inside the shell, so navigation survives. -->
      <UErrorView v-if="hasError" />
      <main
        v-else
        class="mx-auto w-full min-w-0 max-w-3xl flex-1 px-4 pb-16 pt-5 sm:px-6 lg:pt-10"
        :style="{ paddingBottom: 'calc(4rem + var(--safe-area-bottom))' }"
      >
        <RouterView />
        <PageNav v-if="!isFocusMode" />
      </main>
    </div>

    <!-- Mobile drawer: the same table of contents at touch size. -->
    <USlideover
      v-model:open="mobileNavOpen"
      side="left"
      title="DailyCzech"
      data-testid="mobile-nav"
      :ui="{
        overlay: 'lg:hidden',
        content: 'max-w-80 bg-elevated lg:hidden',
        header: 'min-h-14 px-4 py-2',
        body: 'px-3 py-2',
        footer: 'p-3 border-t border-default',
        close: 'static ms-auto size-11 justify-center',
      }"
    >
      <template #title>
        <span class="flex items-center gap-2.5">
          <span class="flex size-8 items-center justify-center rounded-[10px] bg-primary text-white">
            <UIcon
              name="i-lucide-book-open-text"
              class="size-[18px]"
            />
          </span>
          <span class="text-base font-bold tracking-tight text-highlighted">DailyCzech</span>
        </span>
      </template>

      <template #body>
        <AppNav @navigate="mobileNavOpen = false" />
      </template>

      <template #footer>
        <UDropdownMenu
          :items="userMenuItems"
          :content="{ side: 'top', align: 'start' }"
          :ui="{ content: 'w-64' }"
          class="w-full min-w-0"
        >
          <UButton
            color="neutral"
            variant="ghost"
            block
            trailing-icon="i-lucide-chevrons-up-down"
            data-testid="mobile-nav-user-button"
            :ui="{ base: 'min-h-11 justify-start gap-3 px-2.5 font-medium', trailingIcon: 'ms-auto size-4 text-dimmed' }"
          >
            <UAvatar
              :alt="displayName"
              size="sm"
              :ui="{ root: 'bg-primary/10', fallback: 'font-semibold text-primary' }"
            />
            <span class="truncate text-sm text-highlighted">{{ displayName }}</span>
          </UButton>
        </UDropdownMenu>
      </template>
    </USlideover>
  </div>
</template>
